import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect, useCallback, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { safeFetch } from "@/lib/safeFetch";
import { addBreadcrumb, captureException, captureMessage } from "@/lib/sentry";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";

export interface NotificationFeedItem {
  id: string;
  type: "activity" | "report" | "system" | "error";
  title?: string;
  message: string;
  created_at: string;
  href?: string | null;
  actor_id?: string | null;
  church_id?: string | null;
  user_email?: string;
  module?: string;
}

const LOCAL_STORAGE_KEY = 'readNotifications';
const MAX_ITEMS = 100;

const DEFAULT_FEED_ITEMS: NotificationFeedItem[] = [];

export function useNotificationsFeed() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const { loading: rolesLoading, isSuperAdmin } = useUserRole();
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const reconnectRef = useRef<NodeJS.Timeout | null>(null);
  const failureCountRef = useRef(0);
  const subscriptionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const readinessTimerRef = useRef<NodeJS.Timeout | null>(null);
  const latestRolesLoadingRef = useRef(rolesLoading);
  const latestSuperAdminRef = useRef(isSuperAdmin);
  const userId = user?.id ?? null;
  const canSubscribe = Boolean(userId);

  useEffect(() => {
    latestRolesLoadingRef.current = rolesLoading;
  }, [rolesLoading]);

  useEffect(() => {
    latestSuperAdminRef.current = isSuperAdmin;
  }, [isSuperAdmin]);
  
  const [readIds, setReadIds] = useState<Set<string>>(() => {
    if (typeof window === "undefined") {
      return new Set();
    }
    try {
      const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch (error) {
      console.error("Failed to parse read notifications from localStorage", error);
      return new Set();
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(Array.from(readIds)));
    } catch (error) {
      console.error("Failed to save read notifications to localStorage", error);
    }
  }, [readIds]);

  const markAsRead = useCallback((id: string) => {
    setReadIds(prev => new Set([...prev, id]));
  }, []);

  const markAllAsRead = useCallback((notificationIds: string[]) => {
    setReadIds(prev => {
      const newSet = new Set([...prev, ...notificationIds]);
      return newSet;
    });
  }, []);

  const clearReadState = useCallback(() => {
    setReadIds(new Set());
  }, []);

  // Return the query result plus read state management functions
  const queryResult = useQuery({
    queryKey: ["notifications_feed"],
    queryFn: async (): Promise<NotificationFeedItem[]> => {
      console.debug("[useNotificationsFeed] fetching notifications...");
      const notifications: NotificationFeedItem[] = [];

      try {
        // Primary source: audit log activity
        const auditLogs = await safeFetch(
          supabase
            .from('audit_logs')
            .select('id, action, table_name, created_at, user_id')
            .order('created_at', { ascending: false })
            .limit(MAX_ITEMS / 2) // Half limit for audit logs
        );

        const userIds = Array.from(
          new Set(
            auditLogs
              .map((log: any) => log?.user_id)
              .filter((val: any): val is string => Boolean(val))
          )
        );

        const profileLookup = new Map<string, string>();
        if (userIds.length > 0) {
          const profileChunks: string[][] = [];
          for (let i = 0; i < userIds.length; i += 100) {
            profileChunks.push(userIds.slice(i, i + 100));
          }

          for (const chunk of profileChunks) {
            const profiles = await safeFetch(
              supabase
                .from('profiles')
                .select('id, first_name, last_name, email')
                .in('id', chunk)
            );

            profiles.forEach((profile: any) => {
              if (!profile?.id) return;
              const displayName =
                [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim() ||
                profile.email ||
                profile.id;
              profileLookup.set(profile.id, displayName);
            });
          }
        }

        auditLogs.forEach((log: any) => {
          const actorName =
            (log.user_id && profileLookup.get(log.user_id)) ||
            log.user_id ||
            'System';
          const isError = log.action === 'error';
          notifications.push({
            id: `audit_${log.id}`,
            type: isError ? 'error' : 'activity',
            title: `Database Activity: ${log.table_name}`,
            message: `${actorName} ${log.action} ${log.table_name}`,
            created_at: log.created_at,
            href: '/superadmin/audit',
            actor_id: log.user_id,
            church_id: undefined,
            user_email: undefined,
            module: log.table_name
          });
        });

        // Fetch new church signups (system type) safely
        const newChurches = await safeFetch(
          supabase
            .from('churches')
            .select('id, name, created_at')
            .order('created_at', { ascending: false })
            .limit(15)
        );

        newChurches.forEach((church: any) => {
          notifications.push({
            id: `church_${church.id}`,
            type: 'system',
            message: `New church registered: ${church.name}`,
            created_at: church.created_at,
            href: `/church/${church.id}`
          });
        });

        // Try to fetch edge function logs if available
        // Optional: edge function logs (ignore failures if table unavailable)
        const functionLogs = await safeFetch(
          supabase
            .from('edge_function_logs')
            .select('id, function_name, status, created_at, error_message')
            .eq('status', 'error')
            .order('created_at', { ascending: false })
            .limit(15)
        );

        functionLogs.forEach((log: any) => {
          notifications.push({
            id: `function_${log.id}`,
            type: 'error',
            message: `Edge function error: ${log.function_name} - ${log.error_message || 'Unknown error'}`,
            created_at: log.created_at,
            href: '/superadmin/settings'
          });
        });

        // Sort by created_at descending and limit
        const result = notifications
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, MAX_ITEMS);
        console.debug("[useNotificationsFeed] fetched", result.length, "notifications");
        return result;

      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error fetching notifications feed:', error);
        }
        return DEFAULT_FEED_ITEMS;
      }
    },
    placeholderData: DEFAULT_FEED_ITEMS,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000, // Auto-refresh every 60 seconds as fallback
    refetchOnMount: true,
    enabled: canSubscribe,
  });

  // Real-time subscription setup
  useEffect(() => {
    if (!supabase || !canSubscribe) {
      console.debug("[useNotificationsFeed] waiting for user session before subscribing", {
        hasSupabase: Boolean(supabase),
        userReady: Boolean(userId),
      });
      return;
    }

    let active = true;
    let channel: RealtimeChannel | null = null;

    const clearReconnectTimer = () => {
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
        reconnectRef.current = null;
      }
    };

    const clearWaitTimers = () => {
      if (subscriptionTimerRef.current) {
        clearTimeout(subscriptionTimerRef.current);
        subscriptionTimerRef.current = null;
      }
      if (readinessTimerRef.current) {
        clearTimeout(readinessTimerRef.current);
        readinessTimerRef.current = null;
      }
    };

    const handleStatusChange = (status: string) => {
      const statusLevel =
        status === "SUBSCRIBED"
          ? "info"
          : status === "CHANNEL_ERROR" || status === "SUBSCRIPTION_ERROR"
          ? "error"
          : status === "TIMED_OUT" || status === "CLOSED"
          ? "warning"
          : "info";

      addBreadcrumb({
        category: "supabase.realtime",
        message: `notifications status: ${status}`,
        level: statusLevel,
        data: {
          component: "useNotificationsFeed",
          status,
          userId,
        },
      });

      if (!active) return;

      if (status === "SUBSCRIBED") {
        clearReconnectTimer();
        failureCountRef.current = 0;
        setRealtimeConnected(true);
      } else if (status === "CHANNEL_ERROR" || status === "SUBSCRIPTION_ERROR") {
        setRealtimeConnected(false);
        captureMessage(`Notifications realtime connection issue (${status})`, "warning");
        toast({
          title: "Realtime connection issue",
          description: "Notifications may be delayed. Retrying shortly…",
          variant: "destructive",
        });
        scheduleReconnect(status);
      } else if (status === "TIMED_OUT") {
        setRealtimeConnected(false);
        captureMessage("Supabase realtime connection timed out (notifications)", "warning");
        toast({
          title: "Realtime connection timed out",
          description: "Attempting to reconnect…",
          variant: "destructive",
        });
        scheduleReconnect(status);
      } else if (status === "CLOSED") {
        setRealtimeConnected(false);
        captureMessage("Supabase realtime channel closed (notifications)", "info");
        scheduleReconnect(status);
      }
    };

    const createChannel = () =>
      supabase
        .channel("superadmin-notifications")
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "audit_logs" },
          (payload) => {
            const data = payload.new;
            console.info("🧾 New audit log event received:", data);
            addBreadcrumb({
              category: "supabase.realtime",
              message: "notifications payload received",
              level: "info",
              data: {
                table: payload.table,
                schema: payload.schema,
                hasTimestamp: Boolean(data?.created_at),
                component: "useNotificationsFeed",
                userId,
              },
            });
            queryClient.invalidateQueries({ queryKey: ["notifications_feed"] });

            const region = typeof document !== "undefined" ? document.getElementById("live-region") : null;
            if (region) {
              region.textContent = `New activity detected: ${data.table_name ?? "record"} ${data.action}`;
              setTimeout(() => {
                region.textContent = "";
              }, 1000);
            }

            if (!data?.created_at) {
              captureMessage("Realtime notifications payload missing created_at", "warning");
              toast({
                title: "Realtime data incomplete",
                description: "Latest notification missing timestamp. Refresh if feed looks stale.",
                variant: "destructive",
              });
            }

            toast({
              title: `Activity on ${data.table_name ?? "record"}`,
              description: `${data.action} recorded`,
              duration: 4000,
              className: "border border-sky-500 bg-sky-950/70 text-sky-100",
            });
          }
        )
        .subscribe((statusPayload) => { // Received an object not a string
        handleStatusChange(statusPayload.status);
      });

    const scheduleReconnect = (reason: string) => {
      if (!active || reconnectRef.current) return;
      const attempt = failureCountRef.current + 1;
      failureCountRef.current = attempt;
      const delay = Math.min(3000 * attempt, 30000);
      console.warn("[useNotificationsFeed] scheduling reconnect due to:", reason, "attempt:", attempt, "delay:", delay);
      reconnectRef.current = setTimeout(() => {
        reconnectRef.current = null;
        if (!active) return;
        if (channel) {
          try {
            supabase.removeChannel(channel);
          } catch (removeError) {
            console.warn("[useNotificationsFeed] failed to remove channel before reconnect:", removeError);
          }
        }
        beginSubscription();
      }, delay);
    };

    const beginSubscription = () => {
      if (!active) return;

      if (!userId) {
        console.debug("[useNotificationsFeed] subscription aborted - missing user id");
        return;
      }

      if (latestRolesLoadingRef.current || !latestSuperAdminRef.current) {
        readinessTimerRef.current = setTimeout(beginSubscription, 250);
        return;
      }

      subscriptionTimerRef.current = setTimeout(() => {
        if (!active || latestRolesLoadingRef.current || !latestSuperAdminRef.current || !userId) {
          beginSubscription();
          return;
        }

        try {
          channel = createChannel();
        } catch (error) {
          console.error("❌ Failed to establish notifications realtime connection:", error);
          setRealtimeConnected(false);
          captureException(error instanceof Error ? error : new Error(String(error)));
          toast({
            title: "Realtime connection issue",
            description: "Unable to connect to notifications. Retrying shortly…",
            variant: "destructive",
          });
          scheduleReconnect("initial-error");
        }
      }, 800);
    };

    beginSubscription();

    return () => {
      active = false;
      clearWaitTimers();
      clearReconnectTimer();
      failureCountRef.current = 0;
      if (channel) {
        try {
          supabase.removeChannel(channel);
        } catch (error) {
          console.warn("[useNotificationsFeed] failed to remove realtime channel", error);
          captureException(error instanceof Error ? error : new Error(String(error)));
        }
      }
      setRealtimeConnected(false);
    };
  }, [canSubscribe, queryClient, toast, userId]);

  const unreadCount = queryResult.data?.filter(item => !readIds.has(item.id)).length || 0;

  return {
    ...queryResult,
    realtimeConnected,
    unreadCount,
    readIds,
    markAsRead,
    markAllAsRead,
    clearReadState
  };
}
